@if (booking$ | async; as booking) {
  <button
    type="button"
    mat-icon-button
    [class]="
      'environment-booking-trigger state-' + booking.state + (booking.problem ? ' problem-' + booking.problem : '')
    "
    data-testid="environment-booking-trigger"
    [matMenuTriggerFor]="bookingMenu"
    [matTooltip]="triggerLabel(booking)"
    [attr.aria-label]="triggerLabel(booking)"
    (menuOpened)="focusMenu()"
  >
    <span class="booking-status-indicator" aria-hidden="true">
      <mat-icon class="booking-trigger-icon">{{ icon(booking) }}</mat-icon>
    </span>
  </button>
  <span class="booking-live-region" role="status" aria-live="polite">{{ triggerLabel(booking) }}</span>

  <mat-menu #bookingMenu="matMenu" class="environment-booking-menu" xPosition="after">
    <section
      #bookingPanel
      class="environment-booking-panel"
      data-testid="environment-booking-details"
      tabindex="-1"
      [attr.aria-label]="ariaLabel(booking)"
      (keydown.escape)="closeMenu()"
    >
      <header class="booking-header">
        <div>
          <span class="eyebrow">Environment</span>
          <strong>{{ booking.environment }}</strong>
        </div>
        <span [class]="'state-chip state-' + booking.state + (booking.problem ? ' problem-' + booking.problem : '')">{{
          stateLabel(booking)
        }}</span>
      </header>

      @if (showsOwner(booking)) {
        @if (booking.state === 'stale') {
          <p class="booking-detail" data-testid="environment-booking-detail">{{ booking.detail }}</p>
        }
        <section class="booking-section used-by-section" aria-labelledby="booking-used-by-heading">
          <h2 id="booking-used-by-heading" class="booking-section-label">Used by</h2>
          @if (profileUrl(booking); as url) {
            <a
              class="detail-link booking-primary-value"
              [href]="url"
              target="_blank"
              rel="noopener"
              [attr.aria-label]="profileLinkLabel(booking)"
              >{{ booking.profile }}<mat-icon aria-hidden="true">open_in_new</mat-icon></a
            >
          } @else {
            <span class="booking-primary-value">{{ booking.profile }}</span>
          }
        </section>
        <section class="booking-section used-for-section" aria-labelledby="booking-used-for-heading">
          <h2 id="booking-used-for-heading" class="booking-section-label">Used for</h2>
          @if (issueUrl(booking); as url) {
            <a
              class="issue-link"
              [href]="url"
              target="_blank"
              rel="noopener"
              [attr.aria-label]="issueLinkLabel(booking)"
            >
              <span>
                <strong>Issue #{{ booking.issue }}</strong>
                <span
                  class="issue-title"
                  [class.unavailable]="!booking.issueTitle"
                  [attr.title]="booking.issueTitle || null"
                  >{{ booking.issueTitle || 'Issue title unavailable' }}</span
                >
              </span>
              <mat-icon aria-hidden="true">open_in_new</mat-icon>
            </a>
          }
          <div class="booking-context">
            <span>Phase</span><strong>{{ booking.phase }}</strong>
          </div>
        </section>
        <dl class="booking-details">
          <div>
            <dt>Expires (local)</dt>
            <dd>{{ booking.expiresAt ? (booking.expiresAt | date: 'short') : 'Unknown' }}</dd>
          </div>
          @if (booking.candidateSha) {
            <div>
              <dt>Candidate</dt>
              <dd class="candidate">
                @if (candidateUrl(booking); as url) {
                  <a
                    class="detail-link"
                    [href]="url"
                    target="_blank"
                    rel="noopener"
                    [attr.aria-label]="candidateLinkLabel(booking)"
                    [attr.title]="booking.candidateSha"
                  >
                    <span>{{ abbreviatedCandidate(booking) }}</span>
                    <mat-icon aria-hidden="true">open_in_new</mat-icon>
                  </a>
                } @else {
                  {{ booking.candidateSha }}
                }
              </dd>
            </div>
          }
        </dl>
      } @else {
        <p class="booking-detail" data-testid="environment-booking-detail">{{ booking.detail }}</p>
        @if (booking.state === 'reserved') {
          <dl class="booking-details">
            <div>
              <dt>Coding agent</dt>
              <dd>Unknown</dd>
            </div>
          </dl>
        }
      }

      @if (booking.problem) {
        <div class="booking-actions">
          <button type="button" mat-stroked-button data-testid="environment-booking-retry" (click)="checkAgain()">
            <mat-icon aria-hidden="true">refresh</mat-icon>
            Check again
          </button>
        </div>
      }

      @if (leaseWasRead(booking)) {
        <div class="booking-footer-row queue-status" data-testid="environment-booking-queue">
          <mat-icon aria-hidden="true">{{ booking.waitersPresent ? 'group' : 'help_outline' }}</mat-icon>
          <div>
            <strong>Waiting issues</strong>
            @if (booking.waitersPresent) {
              <span>Other issues are waiting for this environment. Their order isn’t assigned.</span>
            } @else {
              <span>It isn’t known whether other issues are waiting.</span>
            }
          </div>
        </div>
      }

      @if (build$ | async; as build) {
        <div class="booking-footer-row deployed-build" data-testid="environment-booking-build">
          <mat-icon aria-hidden="true">rocket_launch</mat-icon>
          <div>
            <strong>Deployed web build</strong>
            <span>
              <a
                class="detail-link candidate"
                [href]="build.url"
                target="_blank"
                rel="noopener"
                [attr.title]="build.commit"
                [attr.aria-label]="'Deployed commit ' + build.shortCommit + '; opens in a new tab'"
                ><span>{{ build.shortCommit }}</span
                ><mat-icon aria-hidden="true">open_in_new</mat-icon></a
              >
              @if (build.branch) {
                <span class="build-branch">{{ build.branch }}</span>
              }
            </span>
            @if (build.builtAt) {
              <span>Built {{ build.builtAt | date: 'medium' }}</span>
            }
            <span>This is what is running now.</span>
          </div>
        </div>
      }
    </section>
  </mat-menu>
}
